import type { RosterChange } from './participant-notices'

/**
 * Who really joined and who really left, as opposed to who reconnected.
 *
 * Presence alone cannot tell them apart. A reload, a network blip, a laptop
 * waking up, a missed heartbeat: each drops the person from Presence and puts
 * them back, and Presence reports that as a leave followed by a join. The
 * announcements used to wait 4 s and then trust it, so any reconnect slower
 * than that — a reload of this app alone usually is — told the room "Priya
 * left", then "Priya joined", about someone who never went anywhere.
 *
 * The server already knows the difference, and says so with `media:peer_left`
 * (lib/signaling_web/departures.ex):
 *   - a deliberate leave (Leave, End, moving to a breakout room) sends it at
 *     once — and it can arrive just BEFORE Presence drops the person;
 *   - any other disconnect is held for a grace window (30 s) and sends it only
 *     if the person has not come back by then. Someone who reconnects in time
 *     never gets one.
 *
 * So a Presence leave only marks someone AWAY. They are announced as having
 * left when `media:peer_left` confirms it — or, if it never comes (a signaling
 * node going down sends none), after FALLBACK_LEAVE_MS. Coming back while away
 * is a reconnect, announced as nothing at all.
 *
 * Coming back can also be under a NEW id. A guest is given a fresh id every
 * time they join (guest.router: `guest_${randomUUID()}`), and a phone browser
 * reloads a tab it discarded in the background, so a guest on a phone with a
 * weak connection rejoins as a stranger again and again: "X joined" (with its
 * sound) at once, and "X left" for the old id half a minute later, every
 * time. Someone arriving under exactly the name of someone away — or leaving
 * while somebody of their exact name is already back — is that same person
 * reconnecting (sameSession). The cost: of two people with the very same name,
 * one dropping and the other arriving inside the window goes unannounced.
 */

/** Longer than the server's 30 s grace, so its `media:peer_left` decides first. */
export const FALLBACK_LEAVE_MS = 35_000

export type DepartureState = {
  /** Dropped from Presence; not yet known to have left. */
  away: ReadonlyMap<string, RosterChange>
  /** Confirmed gone by `media:peer_left` while Presence still listed them. */
  departed: ReadonlySet<string>
}

export const NO_DEPARTURES: DepartureState = { away: new Map(), departed: new Set() }

type Step = { state: DepartureState; announce: RosterChange[] }

/** The same person on a new connection: same non-empty name, same kind of session. */
export function sameSession(a: RosterChange, b: RosterChange): boolean {
  return a.id !== b.id && a.name.trim() !== '' && a.name.trim() === b.name.trim() && a.shareOnly === b.shareOnly
}

/**
 * People Presence lists as arriving. Someone who was away is back from a
 * reconnect — not announced. Anyone else is a real arrival, including someone
 * who deliberately left and has now come back.
 */
export function onPresenceJoined(
  state: DepartureState,
  joined: readonly RosterChange[],
  /** Everyone Presence listed BEFORE this update — an old connection still open. */
  alreadyHere: readonly RosterChange[] = [],
): Step & { replaced: string[] } {
  if (joined.length === 0) return { state, announce: [], replaced: [] }
  const away = new Map(state.away)
  const departed = new Set(state.departed)
  const announce: RosterChange[] = []
  // Old ids a newcomer turned out to be (their fallback timers are moot).
  const replaced: string[] = []
  for (const person of joined) {
    let reconnecting = away.has(person.id) && !departed.has(person.id)
    away.delete(person.id)
    departed.delete(person.id)
    if (!reconnecting) {
      const previous = [...away.values()].find((p) => sameSession(p, person))
      if (previous) {
        away.delete(previous.id)
        replaced.push(previous.id)
        reconnecting = true
      } else if (alreadyHere.some((p) => sameSession(p, person))) {
        // Back before the old connection was even noticed gone (a phone's
        // discarded tab can hold its socket until the heartbeat times out).
        reconnecting = true
      }
    }
    if (!reconnecting) announce.push(person)
  }
  return { state: { away, departed }, announce, replaced }
}

/**
 * People Presence dropped. Already confirmed gone (`media:peer_left` landed
 * first, as it does for a deliberate leave) is announced now; anyone else is
 * only away until the server says otherwise.
 */
export function onPresenceLeft(
  state: DepartureState,
  left: readonly RosterChange[],
  /** Everyone Presence still lists, after this update. */
  stillHere: readonly RosterChange[] = [],
): Step & { nowAway: RosterChange[] } {
  if (left.length === 0) return { state, announce: [], nowAway: [] }
  const away = new Map(state.away)
  const departed = new Set(state.departed)
  const announce: RosterChange[] = []
  const nowAway: RosterChange[] = []
  for (const person of left) {
    if (stillHere.some((p) => sameSession(p, person))) {
      // Their new connection is already in the room: this is the old one
      // closing late, not the person going.
      away.delete(person.id)
      departed.delete(person.id)
    } else if (departed.delete(person.id)) {
      announce.push(person)
    } else if (!away.has(person.id)) {
      away.set(person.id, person)
      nowAway.push(person)
    }
  }
  return { state: { away, departed }, announce, nowAway }
}

/**
 * The server's word that someone's session is over. Announced if they were
 * away; remembered if Presence still lists them (the leave is on its way);
 * nothing if they are neither — already announced, or never known.
 */
export function onPeerLeft(state: DepartureState, peerId: string, stillListed: boolean): Step {
  const person = state.away.get(peerId)
  if (person) {
    const away = new Map(state.away)
    away.delete(peerId)
    return { state: { ...state, away }, announce: [person] }
  }
  if (stillListed && !state.departed.has(peerId)) {
    return { state: { ...state, departed: new Set(state.departed).add(peerId) }, announce: [] }
  }
  return { state, announce: [] }
}

/** No `media:peer_left` arrived in time: someone still away is treated as gone. */
export function onLeaveFallback(state: DepartureState, peerId: string): Step {
  return onPeerLeft(state, peerId, false)
}

/**
 * This client's own Presence snapshot (joining, or rejoining after ITS
 * reconnect): whoever it lists is here, so anyone away among them is back.
 * Returns who was cleared, so their fallback timers can be cancelled.
 */
export function onPresenceSnapshot(state: DepartureState, listed: ReadonlySet<string>): DepartureState & { back: string[] } {
  const back = [...state.away.keys()].filter((id) => listed.has(id))
  if (back.length === 0) return { ...state, back }
  const away = new Map(state.away)
  for (const id of back) away.delete(id)
  return { away, departed: state.departed, back }
}
